import React, { useEffect, useState, useCallback } from 'react';
import {
  FolderOpen, Folder as FolderIcon, FolderPlus, FileText, Image as ImageIcon, Music, Video, Plus, Copy,
  Check, Trash2, ChevronRight, Loader, Home, Download
} from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import Tabs from '../../components/ui/Tabs';

import PageHeader from '../../components/PageHeader';
import { PAGE, PageLoader } from '../../components/crm/ui';
// Same underlying storage module (folders + Document model, real ImageKit
// URLs, per-record association) as every record's own "Documents" panel
// (RelatedFiles.jsx) — this page is just a company-wide browser over the
// same data instead of a separate system with its own model and upload path.
export default function CrmFilesPage() {
  const [allFolders, setAllFolders] = useState([]);
  const [path, setPath] = useState([]); // stack of folder objects; [] = top level
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filesLoading, setFilesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // all, image, document, media
  const [uploading, setUploading] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const currentFolder = path[path.length - 1] || null;
  const currentFolderId = currentFolder?._id || null;

  const fetchAllFolders = useCallback(async () => {
    const { data } = await api.get('/storage/folders');
    setAllFolders(data.data || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        // Idempotent — creates the root scaffold if this company never got
        // one, and imports any file still on the older, separate documents
        // system so nothing already uploaded disappears from view.
        await api.post('/storage/bootstrap');
        await fetchAllFolders();
      } catch (err) {
        console.error('Failed to initialize documents', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchAllFolders]);

  const fetchFiles = useCallback(async (folderId) => {
    if (!folderId) { setFiles([]); return; }
    setFilesLoading(true);
    try {
      const { data } = await api.get('/storage/files', { params: { folderId } });
      setFiles(data.items || []);
    } catch (err) {
      console.error('Failed to load documents', err);
    } finally {
      setFilesLoading(false);
    }
  }, []);

  useEffect(() => { fetchFiles(currentFolderId); }, [currentFolderId, fetchFiles]);

  const childFolders = allFolders.filter(f => (f.parentFolderId || null) === currentFolderId);

  const openFolder = (folder) => setPath(p => [...p, folder]);
  const goToBreadcrumb = (idx) => setPath(p => p.slice(0, idx + 1)); // idx -1 handled by goHome
  const goHome = () => setPath([]);

  const handleCreateFolder = async () => {
    const name = await alertService.prompt('New Folder', 'e.g. Contracts, Marketing Assets, Invoices', { confirmText: 'Create' });
    if (!name) return;
    setCreatingFolder(true);
    try {
      const { data } = await api.post('/storage/folders', {
        name,
        parentFolderId: currentFolderId || undefined,
        module: currentFolder?.module || 'root',
        moduleId: currentFolder?.moduleId || undefined,
      });
      setAllFolders(prev => [...prev, data.data]);
      alertService.toast('success', `Folder "${name}" created`);
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to create folder.');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleDeleteFolder = async (folder, e) => {
    e.stopPropagation();
    const confirmed = await alertService.confirm('Delete Folder?', `Delete "${folder.name}" and mark its documents deleted? This cannot be undone.`, 'warning', 'Delete');
    if (!confirmed) return;
    try {
      await api.delete(`/storage/folders/${folder._id}`);
      setAllFolders(prev => prev.filter(f => f._id !== folder._id));
      alertService.toast('success', 'Folder deleted');
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to delete folder.');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentFolder) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('folderId', currentFolderId);
      form.append('module', currentFolder.module);
      if (currentFolder.moduleId) form.append('moduleId', currentFolder.moduleId);
      form.append('recordName', currentFolder.name);
      await api.post('/storage/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      alertService.toast('success', 'File uploaded successfully!');
      fetchFiles(currentFolderId);
    } catch (err) {
      alertService.error('Upload Failed', err.response?.data?.error || 'Failed to complete upload.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    const confirmed = await alertService.confirm(
      'Delete Document?',
      'This will permanently delete this document and its version history.',
      'warning',
      'Delete'
    );
    if (!confirmed) return;

    try {
      await api.delete(`/storage/files/${fileId}`);
      alertService.toast('success', 'Document deleted.');
      fetchFiles(currentFolderId);
    } catch (err) {
      alertService.error('Error', 'Failed to delete file.');
    }
  };

  const copyUrl = (url, id) => {
    navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
    alertService.toast('success', 'URL copied to clipboard!');
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const fileKind = (mimeType) => {
    if (!mimeType) return 'other';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('audio/')) return 'audio';
    if (mimeType.startsWith('video/')) return 'video';
    return 'document';
  };

  const getFileIcon = (mimeType) => {
    switch (fileKind(mimeType)) {
      case 'image': return <ImageIcon size={20} className="text-[#0176D3]" />;
      case 'audio': return <Music size={20} className="text-[#2E844A]" />;
      case 'video': return <Video size={20} className="text-[#A094ED]" />;
      default: return <FileText size={20} className="text-[#FF9900]" />;
    }
  };

  const filteredFiles = files.filter(f => {
    if (activeTab === 'all') return true;
    if (activeTab === 'image') return fileKind(f.mimeType) === 'image';
    if (activeTab === 'document') return fileKind(f.mimeType) === 'document';
    if (activeTab === 'media') return ['audio', 'video'].includes(fileKind(f.mimeType));
    return true;
  });

  return (
    <div className={PAGE}>
      <PageHeader
        title="Documents & Files"
        subtitle="Media library with full versioning, integrated directly with your configured ImageKit account."
        icon={FolderOpen}
        actions={<>
<button
            onClick={handleCreateFolder}
            disabled={creatingFolder}
            className="h-9 px-4 border border-[#D8DDE6] rounded text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] transition-all flex items-center gap-2 disabled:opacity-50 whitespace-nowrap shrink-0"
          >
            {creatingFolder ? <Loader size={14} className="animate-spin" /> : <FolderPlus size={14} />}
            New Folder
          </button>
          <label className={`h-9 px-4 rounded text-[13px] font-bold transition-all shadow-[0_2px_8px_rgba(0,0,0,0.16)] flex items-center gap-2 ${currentFolder ? 'bg-[#0176D3] text-white hover:bg-[#014486] cursor-pointer' : 'bg-[#F3F2F2] text-[#B0B0B0] cursor-not-allowed'}`}>
            {uploading ? (
              <><Loader size={14} className="animate-spin" /> Uploading…</>
            ) : (
              <><Plus size={14} /> Upload New File</>
            )}
            <input
              type="file"
              onChange={handleUpload}
              disabled={uploading || !currentFolder}
              className="hidden"
            />
          </label>
  </>}
      />

      {/* Breadcrumbs */}
      <div className="flex items-center gap-1.5 text-[13px] flex-wrap">
        <button onClick={goHome} className={`flex items-center gap-1 px-2 py-1 rounded font-bold ${!currentFolder ? 'text-[#0176D3]' : 'text-[#706E6B] hover:text-[#080707]'}`}>
          <Home size={13} /> All Folders
        </button>
        {path.map((folder, idx) => (
          <React.Fragment key={folder._id}>
            <ChevronRight size={13} className="text-[#D8DDE6]" />
            <button
              onClick={() => goToBreadcrumb(idx)}
              className={`px-2 py-1 rounded font-bold ${idx === path.length - 1 ? 'text-[#0176D3]' : 'text-[#706E6B] hover:text-[#080707]'}`}
            >
              {folder.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Type tabs — only meaningful once inside a folder with files */}
      {currentFolder && (
        <Tabs tabs={['all', 'image', 'document', 'media'].map((tab) => ({ key: tab, label: tab === 'media' ? 'Audio & Video' : tab.charAt(0).toUpperCase() + tab.slice(1) + (tab === 'all' ? '' : 's') }))} value={activeTab} onChange={setActiveTab} />
      )}

      {loading ? (
        <PageLoader inline />
      ) : (
        <div className="space-y-4">
          {/* Subfolders in the current location */}
          {childFolders.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {childFolders.map(folder => (
                <button
                  key={folder._id}
                  onClick={() => openFolder(folder)}
                  className="group relative p-4 bg-white border border-[#D8DDE6] rounded-lg hover:border-[#0176D3] hover:shadow-[0_2px_8px_rgba(0,0,0,0.16)] transition-all text-left"
                >
                  <FolderIcon size={22} className="text-[#0176D3] mb-2" />
                  <p className="text-[12px] font-bold text-[#080707] truncate">{folder.name}</p>
                  {!folder.isSystem && (
                    <span
                      onClick={(e) => handleDeleteFolder(folder, e)}
                      className="absolute top-2 right-2 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-[#FDE7E9] text-[#706E6B] hover:text-[#BA0517] transition-all"
                      title="Delete folder"
                    >
                      <Trash2 size={12} />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Files in the current folder */}
          {!currentFolder ? (
            childFolders.length === 0 && (
              <div className="sf-card text-center py-16 space-y-3">
                <FolderOpen size={40} className="mx-auto text-[#D8DDE6]" />
                <p className="text-[13px] font-medium text-[#706E6B]">No folders yet — create one to start uploading.</p>
              </div>
            )
          ) : filesLoading ? (
            <div className="py-12 flex justify-center"><Loader size={22} className="animate-spin text-[#0176D3]" /></div>
          ) : filteredFiles.length > 0 ? (
            <div className="sf-table-container">
              <div className="overflow-x-auto">
                <table className="sf-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Name</th>
                      <th>Size</th>
                      <th>Version</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredFiles.map((file) => (
                      <tr key={file._id} className="group">
                        <td>
                          {fileKind(file.mimeType) === 'image' && (file.thumbnailUrl || file.previewUrl) ? (
                            <img
                              src={file.thumbnailUrl || file.previewUrl}
                              alt={file.fileName}
                              className="w-9 h-9 rounded object-cover border border-[#D8DDE6]"
                            />
                          ) : (
                            getFileIcon(file.mimeType)
                          )}
                        </td>
                        <td className="font-bold text-[#080707] break-all">{file.fileName}</td>
                        <td className="text-[#706E6B]">{formatSize(file.size)}</td>
                        <td><span className="sf-badge sf-badge-neutral">V{file.currentVersion}</span></td>
                        <td className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => copyUrl(file.downloadUrl, file._id)}
                              className="h-8 w-8 flex items-center justify-center bg-white border border-[#D8DDE6] rounded hover:bg-[#F3F2F2] text-[#706E6B] transition-all"
                              title="Copy URL"
                              aria-label="Copy URL"
                            >
                              {copiedId === file._id ? <Check size={14} className="text-[#2E844A]" /> : <Copy size={14} />}
                            </button>
                            <a
                              href={file.downloadUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              download={file.fileName}
                              title="Download"
                              className="h-8 w-8 flex items-center justify-center bg-white border border-[#D8DDE6] rounded hover:bg-[#F3F2F2] hover:border-[#0176D3] hover:text-[#0176D3] text-[#706E6B] transition-all"
                            >
                              <Download size={14} />
                            </a>
                            <button
                              onClick={() => handleDelete(file._id)}
                              className="h-8 w-8 flex items-center justify-center bg-white border border-[#D8DDE6] rounded hover:bg-[#FDE7E9] hover:border-[#BA0517] hover:text-[#BA0517] text-[#706E6B] transition-all"
                              title="Delete"
                              aria-label="Delete"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="sf-card text-center py-16 space-y-3">
              <FolderOpen size={40} className="mx-auto text-[#D8DDE6]" />
              <p className="text-[13px] font-medium text-[#706E6B]">No documents in this folder yet.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
